import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/buttons/Button';
import { useAuth } from '../../contexts/AuthContext';
import { Card } from '../../components/cards/Card';
import { Badge } from '../../components/common/Badge';
import {
  Check, X, HelpCircle, ChevronDown, ChevronUp,
  CreditCard, ShieldCheck, Zap, BarChart3, Users,
  Building, CheckCircle2, Clock, FileCheck
} from 'lucide-react';

import { SEOHead } from '../../components/seo/SEOHead';
import { Breadcrumb } from '../../components/seo/Breadcrumb';

export const Pricing: React.FC = () => {
  const { isLandlordEnabled } = useAuth();
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const plans = [
    {
      name: 'Early Access', // Renamed from Starter
      price: 'Free',
      period: 'limited time',
      description: 'Full access to our platform features while we grow.',
      features: [
        '3 Active Listings', // Upgraded from 1
        'Tenant Screening',  // Unlocked
        'Performance Analytics', // Unlocked
        'Digital Leases', // Unlocked
        'Priority Support', // Unlocked
        'Manual Verification'
      ],
      cta: 'Get Started',
      highlight: true // Highlighted since it's the only one
    },
    // Hidden Plans (Pro & Agency) - Kept in code but not rendered
    {
      name: 'Pro',
      price: 'R499',
      period: 'per month',
      description: 'For serious landlords growing their portfolio.',
      features: ['5 Active Listings', 'Tenant Screening', 'Priority Support', 'Featured Search Placement', 'Performance Analytics'],
      cta: 'Choose Pro',
      highlight: true
    },
    {
      name: 'Agency',
      price: 'R1,699',
      period: 'per month',
      description: 'Volume tools for property managers and agencies.',
      features: ['Unlimited Listings', 'Dedicated Account Manager', 'Top of Search Results', 'API Access', 'Bulk Upload Tools'],
      cta: 'Contact Sales',
      highlight: false
    }
  ];

  const comparisonFeatures = [
    { name: 'Active Listings', starter: '1', pro: '5', agency: 'Unlimited' },
    { name: 'Photo Uploads', starter: '5 per listing', pro: '15 per listing', agency: 'Unlimited' },
    { name: 'Listing Duration', starter: '30 days', pro: 'Unlimited', agency: 'Unlimited' },
    { name: 'Tenant Screening', starter: false, pro: true, agency: true },
    { name: 'Featured Badge', starter: false, pro: true, agency: true },
    { name: 'API Access', starter: false, pro: false, agency: true },
    { name: 'Support', starter: 'Email', pro: 'Priority Email', agency: 'Phone + Dedicated Agent' },
  ];

  const faqs = [
    { q: "Is it really free?", a: "Yes, for the Early Access period, you can list up to 3 properties and use all our premium features for free." },
    { q: "What happens after the Early Access period?", a: "We will introduce paid tiers later, but you will be notified well in advance. Your current listings will remain active." },
    { q: "How many properties can I list?", a: "Currently, you are limited to a maximum of 3 active listings per account." },
    { q: "Do you take commission on rent?", a: "No. We do not charge commissions on your rental income." }
  ];

  return (
    <>
      <SEOHead
        title="Pricing Plans for Landlords & Agents | IndigoBeans Rooms"
        description="Affordable listing packages for landlords and agents. List your student accommodation starting from R0. Transparent pricing, no hidden fees."
        canonical="https://rooms.indigobeans.co.za/pricing"
      />
      <div className="min-h-screen bg-white font-sans">
        {/* Breadcrumb */}
        <div className="bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 py-2">
            <Breadcrumb items={[{ name: 'Pricing', url: '/pricing' }]} />
          </div>
        </div>

        {/* 2. Page Header Section */}
        <section className="relative py-24 text-center text-white overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=1950&q=80')] bg-cover bg-center"></div>
          <div className="absolute inset-0 bg-[#2f5c96]/90 mix-blend-multiply"></div>
          <div className="relative z-10 max-w-4xl mx-auto px-4">
            <Badge color="blue" className="mb-4 bg-white/20 text-white border-none">Early Access</Badge>
            <h1 className="text-4xl lg:text-5xl font-extrabold tracking-tight mb-4 text-white">
              Simple, Transparent <span className="text-blue-200">Pricing</span>
            </h1>
            <p className="text-xl text-blue-100 max-w-2xl mx-auto">
              Join now and get full access to all features for free (Limit 3 listings).
            </p>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-24">

          {/* 3. Pricing Plans Section */}
          <section>
            {!isLandlordEnabled ? (
              <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200">
                <h3 className="text-2xl font-bold text-gray-500 mb-2">Registration Closed</h3>
                <p className="text-gray-500">New landlord registrations are currently paused. Please check back later.</p>
              </div>
            ) : (
              <div className="flex justify-center max-w-6xl mx-auto">
                {/* Only rendering the first plan (Early Access) */}
                {plans.slice(0, 1).map((plan, i) => (
                  <Card
                    key={i}
                    className={`flex flex-col p-8 relative transition-transform duration-300 hover:-translate-y-1 w-full max-w-md ${plan.highlight
                      ? 'border-2 border-[#2f5c96] shadow-xl z-10'
                      : 'border border-gray-100 shadow-md'
                      }`}
                  >
                    {plan.highlight && (
                      <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#2f5c96] text-white px-4 py-1 rounded-full text-sm font-bold uppercase tracking-wide">
                        Best Value
                      </div>
                    )}
                    <div className="mb-6 text-center">
                      <h3 className="text-2xl font-bold text-gray-900">{plan.name}</h3>
                      <p className="text-gray-500 mt-2 text-sm">{plan.description}</p>
                    </div>
                    <div className="mb-8 text-center">
                      <span className="text-4xl font-extrabold text-gray-900">{plan.price}</span>
                      <span className="text-gray-500 font-medium ml-2">{plan.price !== 'Free' && plan.period}</span>
                    </div>
                    <ul className="space-y-4 mb-8 flex-1">
                      {plan.features.map((feature, j) => (
                        <li key={j} className="flex items-start">
                          <CheckCircle2 className={`w-5 h-5 mr-3 flex-shrink-0 ${plan.highlight ? 'text-[#2f5c96]' : 'text-green-500'}`} />
                          <span className="text-gray-600 text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Link to="/register">
                      <Button
                        variant={plan.highlight ? 'primary' : 'outline'}
                        className="w-full"
                      >
                        {plan.cta}
                      </Button>
                    </Link>
                  </Card>
                ))}
              </div>
            )}
          </section>

          {/* 4. Plan Comparison Table - HIDDEN FOR NOW */}
          {/* 
        <section className="hidden md:block">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">Feature Comparison</h2>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
             ... (Table Hidden) ...
          </div>
        </section>
        */}

          {/* 5. What's Included (Feature Highlights) */}
          <section>
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900">Everything you need to rent faster</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                { icon: ShieldCheck, title: "Tenant Screening", desc: "Verify student status and history automatically." },
                { icon: BarChart3, title: "Performance Data", desc: "Track views, clicks, and inquiry rates in real-time." },
                { icon: Zap, title: "Instant Inquiries", desc: "Get WhatsApp or Email notifications immediately." },
                { icon: FileCheck, title: "Digital Leases", desc: "Generate and sign standard lease agreements online." }
              ].map((feature, i) => (
                <div key={i} className="text-center p-6 rounded-xl bg-gray-50 border border-gray-100 hover:shadow-md transition-shadow">
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm text-[#c19cca]">
                    <feature.icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-sm text-gray-600">{feature.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* 6 & 7. Payment Info & Activation Flow */}
          <section className="bg-blue-50 rounded-3xl p-8 lg:p-12 border border-blue-100">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">How Approval Works</h2>
                <p className="text-gray-600 mb-8 leading-relaxed">
                  We value quality over quantity. Your free Early Access helps us maintain a verified, safe marketplace for students.
                  You won't be charged, but verification is still required.
                </p>
                <div className="space-y-6">
                  {[
                    { title: "Register & Profile", desc: "Create your landlord account and upload verification docs." },
                    { title: "Automatic Free Plan", desc: "You are automatically enrolled in the Early Access plan." },
                    { title: "Admin Verification", desc: "Our team reviews your details (usually within 24h)." },
                    { title: "Go Live", desc: "Once approved, list up to 3 properties immediately." }
                  ].map((step, i) => (
                    <div key={i} className="flex gap-4">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#2f5c96] text-white flex items-center justify-center font-bold text-sm">
                        {i + 1}
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900">{step.title}</h4>
                        <p className="text-sm text-gray-600">{step.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
                <h3 className="font-bold text-gray-900 mb-6 flex items-center">
                  <CreditCard className="w-5 h-5 mr-2 text-[#2f5c96]" /> Billing Information
                </h3>
                <div className="space-y-4 text-sm text-gray-600">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <p>No credit card required for registration.</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <p>Enjoy all Pro features during Early Access.</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <p>3 Listing Cap applies to all accounts.</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* 8. Trust Section */}
          <section className="border-t border-gray-100 pt-16">
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div>
                <ShieldCheck className="w-8 h-8 mx-auto text-gray-400 mb-3" />
                <h4 className="font-bold text-gray-700">Secure Platform</h4>
                <p className="text-xs text-gray-500">Bank-grade encryption for all data.</p>
              </div>
              <div>
                <Users className="w-8 h-8 mx-auto text-gray-400 mb-3" />
                <h4 className="font-bold text-gray-700">Verified Students</h4>
                <p className="text-xs text-gray-500">We verify student emails and IDs.</p>
              </div>
              <div>
                <HelpCircle className="w-8 h-8 mx-auto text-gray-400 mb-3" />
                <h4 className="font-bold text-gray-700">Human Support</h4>
                <p className="text-xs text-gray-500">Real people helping you succeed.</p>
              </div>
            </div>
          </section>

          {/* 9. Call to Action */}
          <section className="bg-[#2f5c96] rounded-3xl p-12 text-center text-white shadow-xl relative overflow-hidden">
            <div className="relative z-10 max-w-2xl mx-auto">
              <h2 className="text-3xl font-bold mb-6">Ready to fill your rooms?</h2>
              <p className="text-blue-100 mb-8 text-lg">
                Join hundreds of landlords connecting with students today.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/register">
                  <Button className="w-full sm:w-auto bg-white text-[#2f5c96] hover:bg-gray-100 border-none px-8 py-3 text-lg shadow-lg">
                    Get Started Free
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline" className="w-full sm:w-auto border-white text-white hover:bg-white/10 px-8 py-3 text-lg">
                    Contact Sales
                  </Button>
                </Link>
              </div>
            </div>
            {/* Decorative Pattern */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl translate-x-1/3 -translate-y-1/3"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full blur-3xl -translate-x-1/3 translate-y-1/3"></div>
          </section>

          {/* 10. FAQ Section */}
          <section className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-10">Frequently Asked Questions</h2>
            <div className="space-y-4">
              {faqs.map((faq, idx) => (
                <div key={idx} className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                  <button
                    onClick={() => toggleFaq(idx)}
                    className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-semibold text-gray-800">{faq.q}</span>
                    {openFaq === idx ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                  </button>
                  {openFaq === idx && (
                    <div className="p-5 bg-gray-50 border-t border-gray-100 text-gray-600 text-sm leading-relaxed animate-fadeIn">
                      {faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

        </div>
      </div>
    </>
  );
};